import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit2, Eye, Loader2, Film, Play, Link2, FileVideo, AlertCircle, CheckCircle2, Clock, Rocket, EyeOff, Upload, Send } from 'lucide-react';
import { PendingUpload, UploadStatus } from '../types/uploads';
import ContractGatePrompt from '../components/ContractGatePrompt';
import GuildProjectLinkCard from '../components/GuildProjectLinkCard';
import TheatricalListingCard from '../components/TheatricalListingCard';
import CollaboratorSplitCard from '../components/CollaboratorSplitCard';
import {
  CONTRACT_GATE_MESSAGE,
  requiresContractForPublishing,
} from '../lib/contractGate';
import {
  canPublishWork,
  canSubmitForReview,
  getUploadStatusLabel,
  getWorkLifecycleStatus,
} from '../lib/workStatus';

interface Movie {
  id: string;
  title: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  content_page_state: string | null;
  poster_url: string | null;
  trailer_video_url: string | null;
  view_count: number;
  created_at: string;
  published_at: string | null;
}

interface Genre {
  id: string;
  name: string;
}

interface VideoRecord {
  id: string;
  mux_status: string;
  mux_playback_id: string | null;
}

interface EditForm {
  title: string;
  description: string;
  trailer_video_url: string;
}

export default function MovieDetail() {
  const { movieId } = useParams<{ movieId: string }>();
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [movie, setMovie] = useState<Movie | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedGenreIds, setSelectedGenreIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({ title: '', description: '', trailer_video_url: '' });
  const [saving, setSaving] = useState(false);

  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [uploadingPoster, setUploadingPoster] = useState(false);

  // Linked asset
  const [linkedAsset, setLinkedAsset] = useState<PendingUpload | null | undefined>(undefined);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Video record (from videos table — Mux readiness)
  const [video, setVideo] = useState<VideoRecord | null>(null);

  // Publish / unpublish confirmation dialogs
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Attach existing asset
  const [attachOpen, setAttachOpen] = useState(false);
  const [availableAssets, setAvailableAssets] = useState<PendingUpload[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (movieId && publisher?.id) fetchAll();
  }, [movieId, publisher?.id]);

  const fetchAll = async () => {
    if (!movieId || !publisher?.id) return;
    setLoading(true);
    try {
      const [movieRes, genresRes, selectedRes, assetRes, videoRes] = await Promise.all([
        supabase
          .from('works')
          .select('id, title, description, status, content_page_state, poster_url, trailer_video_url, view_count, created_at, published_at')
          .eq('id', movieId)
          .eq('publisher_id', publisher.id)
          .single(),
        supabase.from('genres').select('id, name').order('name'),
        supabase.from('work_genres').select('genre_id').eq('work_id', movieId),
        supabase
          .from('pending_uploads')
          .select('*')
          .eq('work_id', movieId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('videos')
          .select('id, mux_status, mux_playback_id')
          .eq('parent_id', movieId)
          .eq('parent_type', 'work')
          .maybeSingle(),
      ]);

      if (movieRes.error) throw movieRes.error;
      setMovie(movieRes.data as unknown as Movie | null);
      setGenres(genresRes.data || []);
      setSelectedGenreIds((selectedRes.data || []).map((r: { genre_id: string }) => r.genre_id));
      setLinkedAsset(assetRes.data as PendingUpload | null);
      setVideo(videoRes.data as VideoRecord | null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load movie');
    } finally {
      setLoading(false);
    }
  };

  const publishCheck = () =>
    canPublishWork({
      workStatus: movie?.status ?? 'draft',
      linkedUpload: linkedAsset ?? null,
      muxReady: video?.mux_status === 'ready',
    });

  const handleSubmitForReview = async () => {
    if (!linkedAsset) return;
    try {
      const { error } = await supabase
        .from('pending_uploads')
        .update({ upload_status: 'human_review' })
        .eq('id', linkedAsset.id);
      if (error) throw error;
      setLinkedAsset({ ...linkedAsset, upload_status: 'human_review' });
      toast.success('Submitted for review');
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit for review');
    }
  };

  const contractBlocked = requiresContractForPublishing(publisher);

  const handlePublish = async () => {
    if (!movie) return;
    if (contractBlocked) {
      toast.error(CONTRACT_GATE_MESSAGE);
      return;
    }
    setPublishing(true);
    try {
      const { error } = await supabase.from('works').update({
        status: 'published',
        content_page_state: 'released',
        published_at: new Date().toISOString(),
      }).eq('id', movie.id);

      if (error) throw error;

      setMovie(prev => prev ? {
        ...prev,
        status: 'published',
        content_page_state: 'released',
        published_at: new Date().toISOString(),
      } : prev);
      toast.success('Content published!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish');
    } finally {
      setPublishing(false);
      setPublishConfirmOpen(false);
    }
  };

  const handleUnpublish = async () => {
    if (!movie) return;
    setPublishing(true);
    try {
      const { error } = await supabase.from('works').update({
        status: 'draft',
      }).eq('id', movie.id);

      if (error) throw error;

      setMovie(prev => prev ? { ...prev, status: 'draft' } : prev);
      toast.success('Content unpublished');
    } catch (err: any) {
      toast.error(err.message || 'Failed to unpublish');
    } finally {
      setPublishing(false);
      setUnpublishConfirmOpen(false);
    }
  };

  // ── Asset management ───────────────────────────────────────────────
  const openAttachDialog = async () => {
    if (!publisher?.id) return;
    setAttachOpen(true);
    setLoadingAssets(true);
    try {
      const { data, error } = await supabase
        .from('pending_uploads')
        .select('*')
        .eq('publisher_id', publisher.id)
        .is('work_id', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAvailableAssets((data || []) as unknown as PendingUpload[]);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load assets');
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleAttachAsset = async (asset: PendingUpload) => {
    if (!movieId) return;
    setAttaching(true);
    try {
      const { error } = await supabase
        .from('pending_uploads')
        .update({ work_id: movieId })
        .eq('id', asset.id);
      if (error) throw error;
      setLinkedAsset({ ...asset, work_id: movieId });
      setAttachOpen(false);
      toast.success('Asset linked to this title');
    } catch (err: any) {
      toast.error(err.message || 'Failed to attach asset');
    } finally {
      setAttaching(false);
    }
  };

  const handleDetachAsset = async () => {
    if (!linkedAsset) return;
    try {
      const { error } = await supabase
        .from('pending_uploads')
        .update({ work_id: null })
        .eq('id', linkedAsset.id);
      if (error) throw error;
      setLinkedAsset(null);
      toast.success('Asset detached');
    } catch (err: any) {
      toast.error(err.message || 'Failed to detach asset');
    }
  };

  // ── Video preview (supports both R2 and legacy Supabase Storage) ──
  const getVideoUrl = async (upload: PendingUpload): Promise<string | null> => {
    if (!upload.r2_key && upload.storage_path) {
      // Legacy: Supabase Storage
      const { data } = supabase.storage.from('uploads-temp').getPublicUrl(upload.storage_path);
      return data?.publicUrl ?? null;
    }
    if (upload.r2_key) {
      // R2: get signed URL via edge function
      const { data } = await supabase.functions.invoke('r2-signed-url', {
        body: { pending_upload_id: upload.id, purpose: 'preview' },
      });
      return data?.signed_url ?? null;
    }
    return null;
  };

  const handleOpenPreview = async () => {
    if (!linkedAsset) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const url = await getVideoUrl(linkedAsset);
      setPreviewUrl(url);
    } catch {
      toast.error('Failed to load video preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const STATUS_LABELS: Record<UploadStatus, string> = {
    draft: 'Draft', uploading: 'Uploading', scanning: 'Scanning',
    human_review: 'In Review', approved: 'Approved', rejected: 'Rejected',
    ingesting: 'Processing', ingested: 'Live', error: 'Error',
  };

  const lifecycleLabel = movie
    ? getWorkLifecycleStatus({
        workStatus: movie.status,
        linkedUpload: linkedAsset ?? null,
        muxReady: video?.mux_status === 'ready',
      }).replace(/_/g, ' ')
    : '';

  const assetStatusIcon = (status: UploadStatus) => {
    if (['ingested', 'approved'].includes(status)) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (['rejected', 'error'].includes(status)) return <AlertCircle className="h-4 w-4 text-destructive" />;
    if (['uploading', 'scanning', 'human_review', 'ingesting'].includes(status)) return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes) return null;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // ── Edit dialog (metadata only — no status) ───────────────────────
  const openEdit = () => {
    if (!movie) return;
    setEditForm({
      title: movie.title,
      description: movie.description ?? '',
      trailer_video_url: movie.trailer_video_url ?? '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!movie || !editForm.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('works')
        .update({
          title: editForm.title.trim(),
          description: editForm.description.trim() || null,
          trailer_video_url: editForm.trailer_video_url.trim() || null,
        })
        .eq('id', movie.id);
      if (error) throw error;
      setMovie(prev => prev ? {
        ...prev,
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        trailer_video_url: editForm.trailer_video_url.trim() || null,
      } : prev);
      toast.success('Movie updated');
      setEditOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update movie');
    } finally {
      setSaving(false);
    }
  };

  const handlePosterUpload = async () => {
    if (!posterFile || !movie || !publisher?.id) return;
    setUploadingPoster(true);
    try {
      const ext = posterFile.name.split('.').pop();
      const path = `${publisher.id}/movies/${movie.id}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('publisher-assets')
        .upload(path, posterFile, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('publisher-assets').getPublicUrl(path);
      const { error: updateError } = await supabase
        .from('works')
        .update({ poster_url: urlData.publicUrl })
        .eq('id', movie.id);
      if (updateError) throw updateError;
      setMovie(prev => prev ? { ...prev, poster_url: urlData.publicUrl } : prev);
      setPosterFile(null);
      toast.success('Poster updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload poster');
    } finally {
      setUploadingPoster(false);
    }
  };

  const toggleGenre = async (genreId: string) => {
    if (!movie) return;
    const isSelected = selectedGenreIds.includes(genreId);
    try {
      if (isSelected) {
        await supabase.from('work_genres').delete().eq('work_id', movie.id).eq('genre_id', genreId);
        setSelectedGenreIds(prev => prev.filter(id => id !== genreId));
      } else {
        await supabase.from('work_genres').insert({ work_id: movie.id, genre_id: genreId });
        setSelectedGenreIds(prev => [...prev, genreId]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update genres');
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>;
  }

  if (!movie) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Movie not found.</p>
        <Button className="mt-4" onClick={() => navigate('/content/movies')}>Back to Movies</Button>
      </div>
    );
  }

  const publishReady = publishCheck();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content/movies')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold truncate">{movie.title}</h1>
            <Badge
              variant={movie.status === 'published' ? 'default' : 'outline'}
              className={`capitalize ${movie.status === 'published' ? 'bg-green-600/20 text-green-400 border-green-500/30' : ''}`}
            >
              {movie.status}
            </Badge>
          </div>
          {movie.description && (
            <p className="text-muted-foreground mt-1 text-sm line-clamp-2">{movie.description}</p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={openEdit}>
          <Edit2 className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left — Poster + stats */}
        <div className="space-y-4">
          <Card className="partner-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Poster</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {movie.poster_url ? (
                <img
                  src={movie.poster_url}
                  alt={movie.title}
                  className="w-full aspect-[2/3] rounded-lg object-cover"
                />
              ) : (
                <div className="w-full aspect-[2/3] rounded-lg bg-muted flex items-center justify-center">
                  <Film className="h-12 w-12 text-muted-foreground" />
                </div>
              )}
              <Input
                type="file"
                accept="image/*"
                onChange={e => setPosterFile(e.target.files?.[0] ?? null)}
              />
              {posterFile && (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={handlePosterUpload}
                  disabled={uploadingPoster}
                >
                  {uploadingPoster ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Upload Poster
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="partner-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Views</span>
                <span className="ml-auto font-semibold">{(movie.view_count || 0).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right — Metadata + genres */}
        <div className="md:col-span-2 space-y-4">
          <Card className="partner-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground mb-1">Title</p>
                <p className="font-medium">{movie.title}</p>
              </div>
              {movie.description && (
                <div>
                  <p className="text-muted-foreground mb-1">Description</p>
                  <p>{movie.description}</p>
                </div>
              )}
              {movie.trailer_video_url && (
                <div>
                  <p className="text-muted-foreground mb-1">Trailer URL</p>
                  <p className="truncate text-primary">{movie.trailer_video_url}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground mb-1">Status</p>
                <Badge variant={movie.status === 'published' ? 'default' : 'secondary'} className="capitalize">
                  {movie.status}
                </Badge>
              </div>
              {movie.published_at && (
                <div>
                  <p className="text-muted-foreground mb-1">Published</p>
                  <p className="text-xs">{new Date(movie.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="partner-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Genres</CardTitle>
              <CardDescription>Click to toggle genre tags</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {genres.map(genre => (
                  <Badge
                    key={genre.id}
                    variant={selectedGenreIds.includes(genre.id) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${
                      selectedGenreIds.includes(genre.id)
                        ? 'bg-primary hover:bg-primary/80'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleGenre(genre.id)}
                  >
                    {genre.name}
                  </Badge>
                ))}
              </div>
              {selectedGenreIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {selectedGenreIds.length} genre{selectedGenreIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Video Asset card */}
      <Card className="partner-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Video Asset</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/content/new?work_id=${movieId}`)}
              >
                <Upload className="h-3.5 w-3.5 mr-1" />
                Upload Video
              </Button>
              {linkedAsset ? (
                <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={handleDetachAsset}>
                  Detach
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={openAttachDialog}>
                  <Link2 className="h-3.5 w-3.5 mr-1" />
                  Attach Asset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {linkedAsset === undefined ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : linkedAsset === null ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-3 border border-dashed border-border rounded-lg">
              <FileVideo className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">No video attached</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Upload a video from the Asset Pipeline then attach it here, or attach an existing asset.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={openAttachDialog}>
                <Link2 className="h-3.5 w-3.5 mr-1" />
                Attach Existing Asset
              </Button>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                {assetStatusIcon(linkedAsset.upload_status)}
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-sm font-medium truncate">
                    {linkedAsset.metadata?.title || linkedAsset.storage_path?.split('/').pop() || 'Video asset'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <span>{getUploadStatusLabel(linkedAsset.upload_status)}</span>
                    {formatBytes(linkedAsset.file_size) && <span>{formatBytes(linkedAsset.file_size)}</span>}
                  </div>
                  {linkedAsset.upload_status === 'rejected' && linkedAsset.human_review?.notes && (
                    <p className="text-xs text-destructive mt-1">Rejected: {linkedAsset.human_review.notes}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {canSubmitForReview(linkedAsset) && (
                  <Button size="sm" onClick={handleSubmitForReview}>
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Submit for Review
                  </Button>
                )}
                {(linkedAsset.storage_path || linkedAsset.r2_key) && (
                  <Button size="sm" variant="outline" onClick={handleOpenPreview}>
                    <Play className="h-3.5 w-3.5 mr-1" />
                    Preview
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Guild project link ─────────────────────────────────────── */}
      {movie && <GuildProjectLinkCard workId={movie.id} />}

      {/* ── Theatrical marketplace listing ─────────────────────────── */}
      {movie && <TheatricalListingCard workId={movie.id} />}

      {/* ── Collaborators & revenue split ──────────────────────────── */}
      {movie && <CollaboratorSplitCard workId={movie.id} />}

      {/* ── Publishing section ─────────────────────────────────────── */}
      <Card className={`partner-card ${
        movie.status === 'published'
          ? 'border-green-500/20'
          : publishReady.ok
            ? 'border-publisher-500/20'
            : ''
      }`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Publishing</CardTitle>
          <CardDescription className="capitalize">{lifecycleLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          {movie.status === 'draft' && publishReady.ok && contractBlocked && (
            <ContractGatePrompt />
          )}

          {movie.status === 'draft' && publishReady.ok && !contractBlocked && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
                  <Rocket className="h-4 w-4 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium">Ready to publish</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Video is processed and ready for viewers</p>
                </div>
              </div>
              <Button
                onClick={() => setPublishConfirmOpen(true)}
                className="shrink-0 bg-green-600 hover:bg-green-700 text-white"
              >
                <Rocket className="h-4 w-4 mr-1.5" />
                Publish Now
              </Button>
            </div>
          )}

          {movie.status === 'draft' && !publishReady.ok && (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-500/10">
                <AlertCircle className="h-4 w-4 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cannot publish yet</p>
                <p className="text-xs text-muted-foreground mt-0.5">{publishReady.reason}</p>
              </div>
            </div>
          )}

          {linkedAsset?.upload_status === 'rejected' && (
            <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-sm font-medium text-destructive">Upload rejected</p>
              <p className="text-xs text-muted-foreground mt-1">
                {linkedAsset.human_review?.notes || 'No rejection notes provided.'}
              </p>
              {linkedAsset.appeal_status && (
                <p className="text-xs mt-2">Appeal status: {linkedAsset.appeal_status}</p>
              )}
            </div>
          )}

          {movie.status === 'published' && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-400">Published</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Visible to all subscribers
                    {movie.published_at && (
                      <> &middot; since {new Date(movie.published_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setUnpublishConfirmOpen(true)}
                className="shrink-0"
              >
                <EyeOff className="h-4 w-4 mr-1.5" />
                Unpublish
              </Button>
            </div>
          )}

          {movie.status === 'archived' && (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Archived</p>
                <p className="text-xs text-muted-foreground mt-0.5">This content is archived and not visible to viewers</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Publish confirmation dialog ────────────────────────────── */}
      <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish &ldquo;{movie.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your content visible to all Kinora subscribers immediately.
              You can unpublish at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              disabled={publishing}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
              Publish Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Unpublish confirmation dialog ──────────────────────────── */}
      <AlertDialog open={unpublishConfirmOpen} onOpenChange={setUnpublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish &ldquo;{movie.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your content from the public catalog. Subscribers will no longer
              be able to find or watch it. You can re-publish at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublish}
              disabled={publishing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <EyeOff className="h-4 w-4 mr-1.5" />}
              Unpublish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Video preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{linkedAsset?.metadata?.title || movie.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-3 text-xs">
              {linkedAsset && <span>{STATUS_LABELS[linkedAsset.upload_status]}</span>}
              {formatBytes(linkedAsset?.file_size) && <span>{formatBytes(linkedAsset?.file_size)}</span>}
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading preview...
            </div>
          ) : previewUrl ? (
            <div className="rounded-lg overflow-hidden bg-black">
              <video
                key={previewUrl}
                src={previewUrl}
                controls
                className="w-full max-h-[60vh]"
                preload="metadata"
              >
                Your browser does not support video playback.
              </video>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <FileVideo className="h-8 w-8 mr-2" />
              No video file available.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attach existing asset dialog */}
      <Dialog open={attachOpen} onOpenChange={open => !open && setAttachOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Attach Existing Asset</DialogTitle>
            <DialogDescription>
              Select an unlinked video asset to attach to &ldquo;{movie.title}&rdquo;.
            </DialogDescription>
          </DialogHeader>
          {loadingAssets ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading assets...
            </div>
          ) : availableAssets.length === 0 ? (
            <div className="py-6 text-center space-y-3">
              <p className="text-sm text-muted-foreground">No unlinked assets found.</p>
              <Button size="sm" variant="outline" onClick={() => { setAttachOpen(false); navigate('/content/assets'); }}>
                Go to Asset Pipeline
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {availableAssets.map(asset => {
                const filename = asset.storage_path?.split('/').pop();
                return (
                  <button
                    key={asset.id}
                    disabled={attaching}
                    onClick={() => handleAttachAsset(asset)}
                    className="w-full flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary/60 hover:bg-muted/30 transition-colors text-left disabled:opacity-50"
                  >
                    <FileVideo className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {asset.metadata?.title || filename || 'Untitled asset'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span className="capitalize">{asset.content_type?.replace(/_/g, ' ')}</span>
                        {formatBytes(asset.file_size) && <span>{formatBytes(asset.file_size)}</span>}
                        <span>{STATUS_LABELS[asset.upload_status]}</span>
                      </div>
                    </div>
                    {attaching && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 mt-0.5" />}
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog — metadata only, no status */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Movie</DialogTitle>
            <DialogDescription>Update metadata for this film.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Movie title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Synopsis"
                rows={4}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label>Trailer URL</Label>
              <Input
                value={editForm.trailer_video_url}
                onChange={e => setEditForm(f => ({ ...f, trailer_video_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
